import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/plantus/ProtectedRoute";

import { Navbar } from "@/components/plantus/Navbar";
import { Hero } from "@/components/plantus/Hero";
import { Features } from "@/components/plantus/Features";
import { NewFeatures } from "@/components/plantus/NewFeatures";
import { BetaSignup } from "@/components/plantus/BetaSignup";
import { AiShowcase } from "@/components/plantus/AiShowcase";
import { Audience } from "@/components/plantus/Audience";
import { Demo } from "@/components/plantus/Demo";
import { TrustSection } from "@/components/plantus/TrustSection";
import { Compliance } from "@/components/plantus/Compliance";
import { FAQSection } from "@/components/plantus/FAQ";
import { Contact } from "@/components/plantus/Contact";
import { CtaBottom } from "@/components/plantus/CtaBottom";
import { Footer } from "@/components/plantus/Footer";

import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import PasswortVergessenPage from "@/pages/PasswortVergessen";
import PasswortZuruecksetzenPage from "@/pages/PasswortZuruecksetzen";
import AppLayout from "@/pages/AppLayout";
import Dashboard from "@/pages/Dashboard";
import FieldsPage from "@/pages/Fields";
import ApplicationsPage from "@/pages/Applications";
import NewApplicationPage from "@/pages/NewApplication";
import ApplicationDetailPage from "@/pages/ApplicationDetail";
import TeamPage from "@/pages/Team";
import PricingPage from "@/pages/Pricing";
import CheckoutSuccessPage from "@/pages/CheckoutSuccess";
import BillingPage from "@/pages/Billing";
import ImportPage from "@/pages/Import";
import OnboardingPage from "@/pages/Onboarding";
import ImpressumPage from "@/pages/legal/Impressum";
import DatenschutzPage from "@/pages/legal/Datenschutz";
import AgbPage from "@/pages/legal/Agb";
import KontoLoeschenPage from "@/pages/legal/KontoLoeschen";

const Landing = () => {
  useEffect(() => {
    document.title = "Plantus, Pflanzenschutz. Einfach. Rechtssicher.";
    // Handle #hash-Anker beim initialen Seitenaufruf (BrowserRouter scrollt nicht automatisch)
    const jumpToHash = () => {
      const hash = window.location.hash?.replace("#", "");
      if (!hash) return;
      // Warten bis die Sektionen gemountet sind
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 20) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      tryScroll();
    };
    jumpToHash();
    window.addEventListener("hashchange", jumpToHash);
    return () => window.removeEventListener("hashchange", jumpToHash);
  }, []);

  return (
    <div className="relative min-h-screen bg-bone text-foreground" data-testid="plantus-landing">
      <Navbar />
      <main>
        <Hero />
        <Features />
        <NewFeatures />
        <AiShowcase />
        <Audience />
        <Demo />
        <BetaSignup />
        <TrustSection />
        <Compliance />
        <FAQSection />
        <Contact />
        <CtaBottom />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/passwort-vergessen" element={<PasswortVergessenPage />} />
            <Route path="/passwort-zuruecksetzen" element={<PasswortZuruecksetzenPage />} />
            <Route path="/preise" element={<PricingPage />} />
            <Route path="/preise/erfolg" element={<CheckoutSuccessPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AgbPage />} />
            <Route path="/konto-loeschen" element={<KontoLoeschenPage />} />
            <Route
              path="/willkommen"
              element={
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="fields" element={<FieldsPage />} />
              <Route path="applications" element={<ApplicationsPage />} />
              <Route path="applications/new" element={<NewApplicationPage />} />
              <Route path="applications/:id" element={<ApplicationDetailPage />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="abo" element={<BillingPage />} />
              <Route path="import" element={<ImportPage />} />
            </Route>
          </Routes>
          <Toaster richColors position="bottom-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
